import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Plus,
  Edit,
  Trash2,
  MapPin,
  Loader2,
  RefreshCw,
  Check,
  Lock,
} from "lucide-react";
import { CustomerAddress } from "../../types/supplier";
import { toast } from "react-toastify";
import apiService from "../../services/apiService";
import { validationRules } from "../../lib/validation";
import { getStateCodeFromGSTIN } from "../../lib/gst-utils";
import { STATE_NAME_MAP } from "../../lib/gst-rules";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";
import {
  CUSTOMER_ADDRESS_SAVE_API,
  fetchCustomerAddresses,
  getAddressTypeLabel,
} from "../../lib/customer-addresses";

type AddressType =
  | "registered_address"
  | "branch_address"
  | "warehouse_address"
  | "business_address"
  | "";

const emptyForm = {
  address_type: "" as AddressType,
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  gstin: "",
};

type IndianStateOption = {
  id: number | string;
  state_name?: string;
  gst_state_code?: number | string | null;
};

const EMPTY_STATES: IndianStateOption[] = [];

const inputCls =
  "w-full h-9 pl-3 pr-3 rounded-[8px] border border-[#dde3e4] bg-white text-[13px] font-medium text-[#151b1d] placeholder:text-[#7c8b91] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none transition-all";
const labelCls =
  "block text-[11px] font-semibold uppercase tracking-wider text-[#526066] mb-1";

function normalizeGstStateCode(code: number | string | null | undefined): string {
  if (code === undefined || code === null || code === "") return "";
  return String(code).padStart(2, "0");
}

/**
 * GSTIN optional. Format is always checked when present.
 * State-code match only runs when a state is already selected
 * (so Fetch-before-state / manual-no-GST flows are not blocked).
 */
function validateGstinField(
  value: string,
  selectedStateId?: string,
  indianStates: IndianStateOption[] = []
): string | null {
  const v = value.trim().toUpperCase();
  if (!v) return null;
  if (v.length !== 15) return "GSTIN must be exactly 15 characters";

  const formatError = validationRules.gstin(v);
  if (formatError) return formatError;

  // No state yet — format is enough (Fetch will set state, or user picks it later).
  if (!selectedStateId) return null;

  const selectedState = indianStates.find(
    (state) => String(state.id) === String(selectedStateId)
  );
  if (!selectedState) return null;

  if (
    selectedState.gst_state_code === undefined ||
    selectedState.gst_state_code === null ||
    selectedState.gst_state_code === ""
  ) {
    return null;
  }

  const gstinStateCode = getStateCodeFromGSTIN(v);
  const selectedStateCode = normalizeGstStateCode(selectedState.gst_state_code);

  if (gstinStateCode !== selectedStateCode) {
    return `GSTIN state code (${gstinStateCode}) does not match selected state ${selectedState.state_name ?? ""} (${selectedStateCode})`;
  }

  return null;
}

interface CustomerAddressManagementDialogProps {
  open: boolean;
  add: boolean;
  onOpenChange: (open: boolean) => void;
  addresses?: CustomerAddress[];
  onUpdate?: (addresses: CustomerAddress[]) => void;
  indianStates?: any[];
  customerId: string;
  /** When 4 or 5, at most one `registered_address`; branch/business/warehouse may repeat. */
  sessionEntityType?: number | null;
}


interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
}

function hasRegisteredAddressOtherThan(
  list: CustomerAddress[],
  excludeId?: string
): boolean {
  return list.some(
    (a) =>
      a.address_type === "registered_address" &&
      (excludeId === undefined || String(a.id) !== String(excludeId))
  );
}

export function CustomerAddressManagementDialog({
  open,
  add,
  onOpenChange,
  addresses,
  onUpdate,
  indianStates: indianStatesProp = EMPTY_STATES,
  customerId,
  sessionEntityType,
}: CustomerAddressManagementDialogProps) {
  const { refreshWorkflow } = useCustomerWorkflow();
  const [addressList, setAddressList] = useState<CustomerAddress[]>(addresses ?? []);
  const [indianStates, setIndianStates] = useState<any[]>(indianStatesProp);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(add);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [pincodeError, setPincodeError] = useState("");
  const [isFetchingGst, setIsFetchingGst] = useState(false);
  const [gstVerified, setGstVerified] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [formData, setFormData] = useState({ ...emptyForm });

  const isEditing = !!editingAddress;
  /** GSTIN cannot be changed once an address is being edited. */
  const gstinLocked = isEditing;

  const applyGstinValue = (value: string) => {
    if (gstinLocked) return;
    const upper = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
    setFormData((prev) => ({ ...prev, gstin: upper }));
    setGstVerified(false);
    setCompanyName("");
  };

  const handleFetchGstinClick = async () => {
    if (gstinLocked) return;
    const clean = formData.gstin.trim().toUpperCase();
    if (clean.length !== 15) {
      toast.error("Please enter a valid 15-character GSTIN");
      return;
    }
    const formatError = validationRules.gstin(clean);
    if (formatError) {
      toast.error(formatError);
      return;
    }

    setIsFetchingGst(true);
    try {
      const response: any = await apiService.post(`/supplier/fetch-supplier-gst`, {
        gstin: clean,
      });
      const supplier = response?.supplier;
      if (!supplier) {
        setCompanyName("");
        setGstVerified(false);
        toast.error("No details found for this GSTIN");
        return;
      }

      const stateCode = clean.substring(0, 2);
      const extractedStateName = STATE_NAME_MAP[stateCode] || "";
      let matchedState = indianStates.find(
        (s: IndianStateOption) =>
          s.state_name?.toLowerCase() === extractedStateName.toLowerCase()
      );
      if (!matchedState && extractedStateName) {
        const needle = extractedStateName.toLowerCase().replace(/[^a-z]/g, "");
        matchedState = indianStates.find((s: IndianStateOption) => {
          const hay = (s.state_name || "").toLowerCase().replace(/[^a-z]/g, "");
          return hay.includes(needle) || needle.includes(hay);
        });
      }

      const addr = supplier.address || {};
      const apiState = addr.state != null ? String(addr.state) : "";
      const stateFromApi = indianStates.find(
        (s: IndianStateOption) =>
          String(s.id) === apiState ||
          s.state_name?.toLowerCase() === apiState.toLowerCase()
      );

      const mappedType = (() => {
        const t = String(addr.address_type || "").toLowerCase();
        if (t === "registered_address" || t === "registered office") {
          return "registered_address" as AddressType;
        }
        if (t === "business_address" || t === "business") {
          return "business_address" as AddressType;
        }
        if (t === "branch_address" || t === "branch") {
          return "branch_address" as AddressType;
        }
        if (t === "warehouse_address" || t === "warehouse") {
          return "warehouse_address" as AddressType;
        }
        return "" as AddressType;
      })();

      setFormData((prev) => ({
        ...prev,
        gstin: supplier.gstin || clean,
        address_type: (mappedType || prev.address_type) as AddressType,
        address_line1: addr.address_line1 || prev.address_line1,
        address_line2: addr.address_line2 || prev.address_line2,
        city: addr.city || prev.city,
        state: stateFromApi
          ? String(stateFromApi.id)
          : matchedState
            ? String(matchedState.id)
            : prev.state,
        pincode: addr.pincode ? String(addr.pincode) : prev.pincode,
      }));
      setPincodeError("");
      setCompanyName(supplier.companyName || supplier.company_name || "");
      setGstVerified(true);
      toast.success("GSTIN verified · address details fetched");
    } catch {
      setCompanyName("");
      setGstVerified(false);
      toast.error("Failed to fetch GSTIN details");
    } finally {
      setIsFetchingGst(false);
    }
  };

  const loadAddresses = async () => {
    if (!customerId) {
      setAddressList(addresses ?? []);
      return addresses ?? [];
    }
    setIsLoadingAddresses(true);
    try {
      const list = await fetchCustomerAddresses(customerId);
      setAddressList(list);
      onUpdate?.(list);
      return list;
    } catch {
      const fallback = addresses ?? [];
      setAddressList(fallback);
      return fallback;
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  useEffect(() => {
    if (!(open || add) || !customerId) return;
    void loadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, add, customerId]);

  useEffect(() => {
    if (indianStatesProp.length) {
      setIndianStates(indianStatesProp);
      return;
    }
    if (!(open || add)) return;
    apiService
      .get<any>(`/supplier/get-india-state`)
      .then((response) => setIndianStates(response?.data ?? []))
      .catch(() => setIndianStates([]));
  }, [open, add, indianStatesProp]);

  const resetGstState = () => {
    setGstVerified(false);
    setCompanyName("");
    setIsFetchingGst(false);
  };

  const handleCancel = () => {
    if (add) {
      onOpenChange(false);
      return;
    }
    if (open) {
      setIsAddingNew(false);
      setEditingAddress(null);
      setFormData({ ...emptyForm });
      setPincodeError("");
      resetGstState();
    }
  };

  useEffect(() => {
    if (open) {
      setIsAddingNew(false);
      setEditingAddress(null);
      setFormData({ ...emptyForm });
      setPincodeError("");
      resetGstState();
    }
  }, [open]);

  const enforceSingleRegistered =
    sessionEntityType === 4 || sessionEntityType === 5;

  const handlePincodeChange = (value: string) => {
    const v = value.replace(/\D/g, "").slice(0, 6);
    setPincodeError(
      v && !/^[1-9][0-9]{5}$/.test(v) ? "Invalid pincode (e.g. 400001)" : ""
    );
    setFormData((prev) => ({ ...prev, pincode: v }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.address_type) {
      toast.error("Please select an address type");
      return;
    }
    if (!formData.address_line1.trim()) {
      toast.error("Address line 1 is required");
      return;
    }
    if (!formData.city.trim()) {
      toast.error("City is required");
      return;
    }
    if (!formData.state) {
      toast.error("Please select a state");
      return;
    }
    if (!/^[1-9][0-9]{5}$/.test(formData.pincode)) {
      setPincodeError("Invalid pincode (e.g. 400001)");
      toast.error("Enter a valid 6-digit pincode");
      return;
    }

    // On edit GSTIN is locked — only validate format; allow state/pincode changes.
    const gstinToSave = isEditing
      ? (editingAddress?.gstin || formData.gstin || "").trim().toUpperCase()
      : formData.gstin.trim().toUpperCase();
    const gstinErr = isEditing
      ? gstinToSave
        ? validationRules.gstin(gstinToSave)
        : null
      : validateGstinField(gstinToSave, formData.state, indianStates);
    if (gstinErr) {
      toast.error(gstinErr);
      return;
    }

    if (
      enforceSingleRegistered &&
      formData.address_type === "registered_address" &&
      hasRegisteredAddressOtherThan(addressList, editingAddress?.id)
    ) {
      toast.error(
        "Only one registered address is allowed. Add branch or business addresses separately."
      );
      return;
    }

    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    let response: any;
    try {
      response = await apiService.post<ApiResponse<any>>(
        CUSTOMER_ADDRESS_SAVE_API,
        {
          ...formData,
          gstin: gstinToSave || "",
          id: editingAddress?.id || "",
          customerId: customerId,
        }
      );
      if (response.status == 200) {
        await loadAddresses();
        toast(editingAddress
          ? "Address information has been updated successfully."
          : "New address has been added successfully.");
        setEditingAddress(null);
        setIsAddingNew(false);
        setFormData({ ...emptyForm });
        setPincodeError("");
        resetGstState();
        if (add) onOpenChange(false);
      }
    } catch (err) {
      toast.error('Failed to update Customer')
    } finally {
      setIsSubmitting(false);
      await refreshWorkflow();
    }
  };

  const handleEdit = (address: CustomerAddress) => {
    setEditingAddress(address);
    setPincodeError("");
    setFormData({
      address_type: (address.address_type || "") as AddressType,
      address_line1: address.address_line1,
      address_line2: address.address_line2 || "",
      city: address.city,
      state: address.state != null ? String(address.state) : "",
      pincode: address.pincode,
      gstin: address?.gstin || "",
    });
    setIsAddingNew(true);
    setGstVerified(!!address.gstin);
    setCompanyName("");
  };


  const handleDelete = async (addressId: string) => {
    try {
      const response = await apiService.post<ApiResponse<any>>(
        `/supplier/delete-supplier-address`,
        {
          id: addressId
        }
      );
      if (response.status == 200) {
        await loadAddresses();
        toast("Address has been removed successfully.");
      }
    } catch {
      toast.error("Something when worng, try again!");
    }

  };

  const getAddressLabel = (type?: string) => getAddressTypeLabel(type);

  useEffect(() => {
    if (add) {
      startAddNew();
    }
  }, [add])

  const startAddNew = () => {
    setEditingAddress(null);
    setPincodeError("");
    setFormData({ ...emptyForm });
    setIsAddingNew(true);
    resetGstState();
  };

  const showList = addressList.length > 0 && !add && !isAddingNew;

  const gstinError = (() => {
    const v = formData.gstin.trim();
    if (!v || gstinLocked) return null;
    if (v.length < 15) return null;
    return validateGstinField(formData.gstin, formData.state, indianStates);
  })();

  const showRegisteredAddressType =
    !enforceSingleRegistered ||
    !hasRegisteredAddressOtherThan(addressList, editingAddress?.id);

  useEffect(() => {
    if (
      !showRegisteredAddressType &&
      formData.address_type === "registered_address"
    ) {
      setFormData((prev) => ({
        ...prev,
        address_type: "" as typeof prev.address_type,
      }));
    }
  }, [showRegisteredAddressType, formData.address_type]);

  return (
    <Dialog open={open || add} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] w-[95vw] max-h-[92vh] overflow-y-auto rounded-[14px] border border-[#dde3e4] bg-white p-0 shadow-2xl font-['Montserrat',sans-serif] text-[#151b1d]">
        <DialogTitle className="sr-only">
          {isAddingNew
            ? editingAddress
              ? "Edit Address"
              : "Add New Address"
            : "Manage Addresses"}
        </DialogTitle>

        {/* Header */}
        <div className="border-b border-[#eaeeef] px-6 pb-4 pt-5">
          <div className="flex items-center gap-3 pr-8">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#e1f0f1]">
              <MapPin style={{ width: 18, height: 18 }} className="text-[#3f8b98]" />
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-[#151b1d]">
                {isAddingNew
                  ? editingAddress
                    ? "Edit Address"
                    : "Add New Address"
                  : "Manage Addresses"}
              </h2>
              <p className="mt-0.5 text-[12px] text-[#526066]">
                {isAddingNew
                  ? editingAddress
                    ? "Update address details · GSTIN cannot be changed"
                    : "Optional GSTIN fetch, or enter address manually"
                  : "Customer registered, business, and branch addresses"}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-5 px-6 py-5">
          {isLoadingAddresses && !isAddingNew && (
            <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-[#526066]">
              <Loader2 className="h-4 w-4 animate-spin text-[#3f8b98]" />
              Loading addresses…
            </div>
          )}

          {/* Address list */}
          {!isLoadingAddresses && showList && (
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#7c8b91]">
                  Customer Addresses ({addressList.length})
                </span>
                <button
                  type="button"
                  onClick={startAddNew}
                  className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[7px] bg-[#3f8b98] px-3 text-[12px] font-semibold text-white transition-colors hover:bg-[#2e6b76]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Address
                </button>
              </div>

              {addressList.map((address) => (
                <div
                  key={address.id}
                  className="overflow-hidden rounded-[10px] border border-[#dde3e4] bg-white"
                >
                  <div className="flex items-center justify-between border-b border-[#eaeeef] bg-[#f6f8f8] px-4 py-2.5">
                    <span className="text-[12px] font-semibold text-[#151b1d]">
                      {getAddressLabel(address.address_type)}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleEdit(address)}
                        title="Edit"
                        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[6px] text-[#526066] transition-colors hover:bg-white hover:text-[#3f8b98]"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(address.id)}
                        title="Delete"
                        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[6px] text-[#526066] transition-colors hover:bg-[#fdf1f1] hover:text-[#b0453f]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 px-4 py-3">
                    <p className="text-[13px] font-medium text-[#151b1d]">
                      {address.address_line1}
                    </p>
                    {address.address_line2 ? (
                      <p className="text-[12px] text-[#526066]">{address.address_line2}</p>
                    ) : null}
                    <p className="text-[12px] text-[#526066]">
                      {address.city}
                      {address.city ? ", " : ""}
                      {indianStates.find(
                        (state: IndianStateOption) =>
                          String(state.id) === String(address.state)
                      )?.state_name || address.state}
                      {" — "}
                      {address.pincode}
                    </p>
                    {address.gstin ? (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-[6px] bg-[#e1f0f1] px-2.5 py-1 font-mono text-[11px] font-semibold text-[#2e6b76]">
                        GSTIN: {address.gstin}
                      </div>
                    ) : (
                      <p className="mt-1 text-[12px] text-[#7c8b91]">GSTIN: N/A</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoadingAddresses && !showList && !isAddingNew && (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f6f8f8]">
                <MapPin className="h-5 w-5 text-[#7c8b91]" />
              </div>
              <div className="text-center">
                <p className="text-[13px] font-semibold text-[#151b1d]">
                  No addresses added yet
                </p>
                <p className="mt-0.5 text-[12px] text-[#7c8b91]">
                  Add a registered, business, or branch address
                </p>
              </div>
              <button
                type="button"
                onClick={startAddNew}
                className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[8px] bg-[#3f8b98] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[#2e6b76]"
              >
                <Plus className="h-4 w-4" />
                Add Address
              </button>
            </div>
          )}

          {/* Add / Edit form */}
          {isAddingNew && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Section 1: Optional GSTIN */}
              <div className="space-y-3 rounded-[10px] border border-[#dde3e4] bg-white p-4">
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                      gstVerified || (gstinLocked && formData.gstin)
                        ? "bg-[#e2f2e8] text-[#227a4c]"
                        : "bg-[#3f8b98] text-white"
                    }`}
                  >
                    {gstVerified || (gstinLocked && formData.gstin) ? (
                      <Check className="h-3 w-3 stroke-[2.5]" />
                    ) : (
                      "1"
                    )}
                  </span>
                  <span className="text-[13px] font-semibold text-[#151b1d]">
                    GSTIN (optional)
                  </span>
                </div>

                <div>
                  <label className={labelCls} htmlFor="gstin">
                    GSTIN{" "}
                    {!gstinLocked && (
                      <span className="normal-case tracking-normal text-[#7c8b91]">
                        — fetch to autofill, or skip and enter manually
                      </span>
                    )}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        id="gstin"
                        type="text"
                        maxLength={15}
                        autoComplete="off"
                        placeholder="e.g. 29ABBCA4070B1Z3"
                        value={formData.gstin}
                        onChange={(e) => applyGstinValue(e.target.value)}
                        onPaste={(e) => {
                          if (gstinLocked) return;
                          e.preventDefault();
                          applyGstinValue(e.clipboardData.getData("text") || "");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault();
                        }}
                        disabled={gstinLocked || isFetchingGst}
                        aria-invalid={!!gstinError}
                        aria-describedby={gstinError ? "gstin-error" : undefined}
                        className={`w-full h-9 rounded-[7px] border border-[#dde3e4] bg-white pl-3.5 pr-9 text-[13px] font-medium uppercase text-[#151b1d] outline-none transition-all focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] disabled:cursor-not-allowed disabled:bg-[#f6f8f8] ${
                          gstinError ? "border-[#b0453f]" : ""
                        }`}
                      />
                      {gstinLocked ? (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                          <Lock className="h-3.5 w-3.5 text-[#7c8b91]" />
                        </div>
                      ) : isFetchingGst ? (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                          <Loader2 className="h-4 w-4 animate-spin text-[#3f8b98]" />
                        </div>
                      ) : gstVerified ? (
                        <div className="absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full bg-[#e2f2e8] text-[#227a4c]">
                          <Check className="h-3 w-3 stroke-[2.5]" />
                        </div>
                      ) : null}
                    </div>
                    {!gstinLocked && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleFetchGstinClick();
                        }}
                        disabled={isFetchingGst || formData.gstin.trim().length === 0}
                        className="inline-flex h-9 flex-shrink-0 cursor-pointer items-center gap-1.5 rounded-[7px] border border-[#dde3e4] bg-white px-3.5 text-xs font-medium text-[#151b1d] shadow-sm transition-colors hover:border-[#3f8b98] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RefreshCw
                          className={`h-3.5 w-3.5 text-[#526066] ${
                            isFetchingGst ? "animate-spin text-[#3f8b98]" : ""
                          }`}
                        />
                        <span>{isFetchingGst ? "Fetching…" : "Fetch"}</span>
                      </button>
                    )}
                  </div>

                  {companyName ? (
                    <p className="mt-2 text-[13px] font-medium text-[#151b1d]">
                      {companyName}
                    </p>
                  ) : null}

                  {isFetchingGst && (
                    <div className="mt-2 flex items-center gap-1.5 rounded-[7px] border border-[#b3d9de] bg-[#e1f0f1] px-3 py-1.5 text-xs font-medium text-[#2e6b76]">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Fetching GSTIN details from GST portal…</span>
                    </div>
                  )}

                  {gstinLocked && formData.gstin ? (
                    <div className="mt-2 flex items-center gap-1.5 rounded-[7px] border border-[#eaeeef] bg-[#f6f8f8] px-3 py-1.5 text-xs font-medium text-[#526066]">
                      <Lock className="h-3.5 w-3.5" />
                      <span>GSTIN is locked on edit · state and pincode can still be updated</span>
                    </div>
                  ) : null}

                  {!gstinLocked && gstVerified && !isFetchingGst ? (
                    <div className="mt-2 flex items-center gap-1.5 rounded-[7px] border border-[#bfe9cf] bg-[#e2f2e8] px-3 py-1.5 text-xs font-medium text-[#227a4c]">
                      <Check className="h-3.5 w-3.5" />
                      <span>GSTIN verified · address details auto-fetched (you can still edit)</span>
                    </div>
                  ) : null}

                  {gstinError ? (
                    <p id="gstin-error" className="mt-1 text-[11px] font-medium text-[#b0453f]">
                      {gstinError}
                    </p>
                  ) : !gstinLocked && !gstVerified ? (
                    <p className="mt-1 text-[11px] text-[#7c8b91]">
                      {formData.gstin.length > 0
                        ? `${formData.gstin.length} / 15 characters · click Fetch to autofill`
                        : "Optional. Leave blank to enter address manually"}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Section 2: Address details */}
              <div className="space-y-3 rounded-[10px] border border-[#dde3e4] bg-white p-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#3f8b98] text-xs font-bold text-white">
                    2
                  </span>
                  <span className="text-[13px] font-semibold text-[#151b1d]">
                    Address details
                  </span>
                </div>

                <div>
                  <label className={labelCls}>
                    Address Type <span className="text-[#b0453f]">*</span>
                  </label>
                  <Select
                    value={formData.address_type}
                    onValueChange={(value: string) =>
                      setFormData((prev) => ({
                        ...prev,
                        address_type: value as AddressType,
                      }))
                    }
                  >
                    <SelectTrigger className="h-9 rounded-[8px] border-[#dde3e4] text-[13px] font-medium text-[#151b1d] focus:ring-1 focus:ring-[#3f8b98]">
                      <SelectValue placeholder="Select address type" />
                    </SelectTrigger>
                    <SelectContent className="z-[200] bg-white">
                      {showRegisteredAddressType && (
                        <SelectItem value="registered_address">
                          Registered Address
                        </SelectItem>
                      )}
                      <SelectItem value="business_address">Business Address</SelectItem>
                      <SelectItem value="branch_address">Branch Address</SelectItem>
                      <SelectItem value="warehouse_address">Warehouse Address</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className={labelCls} htmlFor="addressLine1">
                    Address Line 1 <span className="text-[#b0453f]">*</span>
                  </label>
                  <input
                    id="addressLine1"
                    type="text"
                    placeholder="Street, building, locality"
                    value={formData.address_line1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        address_line1: e.target.value,
                      }))
                    }
                    className={inputCls}
                    required
                  />
                </div>

                <div>
                  <label className={labelCls} htmlFor="addressLine2">
                    Address Line 2
                  </label>
                  <input
                    id="addressLine2"
                    type="text"
                    placeholder="Landmark, area (optional)"
                    value={formData.address_line2}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        address_line2: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <label className={labelCls} htmlFor="city">
                      City <span className="text-[#b0453f]">*</span>
                    </label>
                    <input
                      id="city"
                      type="text"
                      placeholder="City"
                      value={formData.city}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, city: e.target.value }))
                      }
                      className={inputCls}
                      required
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      State <span className="text-[#b0453f]">*</span>
                    </label>
                    {indianStates.length > 0 ? (
                      <Select
                        value={formData.state?.toString() || ""}
                        onValueChange={(value: string) =>
                          setFormData((prev) => ({ ...prev, state: value }))
                        }
                      >
                        <SelectTrigger className="h-9 rounded-[8px] border-[#dde3e4] text-[13px] font-medium text-[#151b1d] focus:ring-1 focus:ring-[#3f8b98]">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="z-[200] max-h-60 overflow-y-auto bg-white">
                          {indianStates.map((state) => (
                            <SelectItem key={state.id} value={String(state.id)}>
                              {state.state_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex h-9 items-center gap-2 text-[12px] text-[#7c8b91]">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading states…
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="pincode">
                      Pincode <span className="text-[#b0453f]">*</span>
                    </label>
                    <input
                      id="pincode"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="6-digit PIN"
                      value={formData.pincode}
                      onChange={(e) => handlePincodeChange(e.target.value)}
                      className={`${inputCls} ${pincodeError ? "border-[#b0453f]" : ""}`}
                      required
                    />
                    {pincodeError ? (
                      <p className="mt-1 text-[11px] text-[#b0453f]">{pincodeError}</p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse items-stretch justify-end gap-2.5 border-t border-[#eaeeef] pt-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="h-9 cursor-pointer rounded-[8px] border border-[#dde3e4] bg-white px-4 text-[13px] font-medium text-[#151b1d] transition-colors hover:border-[#3f8b98] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isFetchingGst}
                  className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 rounded-[8px] bg-[#3f8b98] px-5 text-[13px] font-semibold text-white transition-colors hover:bg-[#2e6b76] disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Saving…
                    </>
                  ) : editingAddress ? (
                    "Update Address"
                  ) : (
                    "Add Address"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
